import React, { useContext, useEffect, useMemo } from 'react';
import { history, useLocation, useParams, useSelector } from 'umi';
import { Layout } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import { buriedPointGame, typeIdObj } from '@/pages/home/utils/buriedPoint';
import '@/pages/intelligence/components/LazyReport/index.scss';
import './index.scss';
import { CommonDashboardReport } from '@home/components/CommonDashboardReport';
import { StudioContext } from '@home/pages/studio/context';
import StudioState from '@home/pages/studio/components/StudioState';
import { getLanguage } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import {
  PAGE_TYPE_AUTH_PATH,
  PAGE_TYPE_DATALAB,
  PAGE_TYPE_LOOKER,
  PAGE_TYPE_OUT_PATH,
  PAGE_TYPE_OUT_URL,
  PAGE_TYPE_PDF,
  PAGE_TYPE_TABLEAU,
} from '@/pages/dashboard/_config/constant';
import AuthPath from '@home/pages/studio/authPath/AuthPathContent';
import { getBasePath } from '@/utils/utils';
import Pdf from '@home/components/Pdf';
import Looker from '@home/pages/report/Looker';
import Tableau from '@home/components/Tableau';
import FullScreenBtn from '@home/pages/studio/components/FullScreenBtn';
import { normalizeIframeHeight } from '@home/pages/studio/authPath/configUtils';

const { Content } = Layout;
const REPORT_TAB_QUERY_KEY = 'reportTab';
const MULTI_REPORTS_BASE_PATH = '/multiReports';
const IFRAME_PAGE_TYPES = [
  PAGE_TYPE_PDF,
  PAGE_TYPE_LOOKER,
  PAGE_TYPE_TABLEAU,
];

export default function ReportTabs(props: any) {
  const location = useLocation();
  const { reportKey } = useParams<{ reportKey?: string; }>() || {};
  const { tabList } = props?.config;
  const { currentGame, currentMenu, menuPermission } = useContext(StudioContext);
  const { unified_edition_id: gameId, studio_code: studioId } =
    currentGame || {};
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  const { isEN } = useLocal();
  const multiReportsPath = useMemo(() => {
    const menuPath = String(currentMenu?.path || '').trim();
    if (
      menuPath &&
      (menuPath === MULTI_REPORTS_BASE_PATH ||
        menuPath.startsWith(`${MULTI_REPORTS_BASE_PATH}/`))
    ) {
      return menuPath.startsWith('/') ? menuPath : `/${menuPath}`;
    }
    return reportKey
      ? `${MULTI_REPORTS_BASE_PATH}/${reportKey}`
      : MULTI_REPORTS_BASE_PATH;
  }, [currentMenu?.path, reportKey]);
  const defaultTab = useMemo(() => {
    return (
      tabList.find(
        (item: { pageType: string; }) =>
          item?.pageType !== PAGE_TYPE_OUT_URL &&
          item?.pageType !== PAGE_TYPE_OUT_PATH,
      ) || tabList[0]
    );
  }, [tabList]);
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const activeIdInUrl = searchParams.get(REPORT_TAB_QUERY_KEY);
  const activeTabConfig = useMemo(() => {
    const currentTab = tabList.find(
      (item: { id: string; pageType: string; }) =>
        item?.id === activeIdInUrl &&
        item?.pageType !== PAGE_TYPE_OUT_URL &&
        item?.pageType !== PAGE_TYPE_OUT_PATH,
    );
    return currentTab || defaultTab;
  }, [activeIdInUrl, defaultTab, tabList]);
  const activeId = activeTabConfig?.id;
  const isActiveIframeTab = IFRAME_PAGE_TYPES.includes(activeTabConfig?.pageType);
  const activeIframeHeight = isActiveIframeTab
    ? normalizeIframeHeight(activeTabConfig?.iframeHeight)
    : undefined;
  const activeIframeStyle = activeIframeHeight
    ? { height: activeIframeHeight, minHeight: activeIframeHeight }
    : undefined;
  const tabs = tabList.map(
    (item: {
      id: any;
      nameEn: string;
      nameCn: string;
      logConfig: any;
      pageType: string;
    }) => ({
      id: item?.id,
      pageType: item?.pageType,
      name: getLanguage(isEN, item?.nameEn, item?.nameCn),
      pagePoint: 'studioCBT',
      label: (
        <span className="dbi-sourcelabel-text">
          {getLanguage(isEN, item?.nameEn, item?.nameCn)}
        </span>
      ),
      logConfig: {
        ...item?.logConfig,
        typeId: typeIdObj.studio,
      },
    }),
  );

  useEffect(() => {
    if (!activeId || activeIdInUrl === activeId) {
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    nextParams.set(REPORT_TAB_QUERY_KEY, activeId);
    history.replace(`${location.pathname}?${nextParams.toString()}`);
  }, [activeId, activeIdInUrl, location.pathname, location.search]);

  const onActive = (tab: any) => {
    // out url打开外部url
    if (tab?.pageType === PAGE_TYPE_OUT_URL) {
      window.open(`${tab?.id}`);
      // out path打开系统path
    } else if (tab?.pageType === PAGE_TYPE_OUT_PATH) {
      window.open(`${getBasePath()}${tab?.id}`);
    } else {
      // datalab 报表
      const nextParams = new URLSearchParams(location.search);
      nextParams.set(REPORT_TAB_QUERY_KEY, tab?.id);
      history.replace(`${location.pathname}?${nextParams.toString()}`);
      // 打点
      buriedPointGame(
        { gameName: currentGame?.game_name, ...tab?.logConfig },
        gameId,
        'unified_id',
      );
    }
  };

  // 页面初始化打点信息
  const defaultTabLogConfig = {
    ...activeTabConfig?.logConfig,
    typeId: typeIdObj.studio,
  };
  useEffect(() => {
    if (!activeTabConfig) {
      return;
    }
    // 页面初始化打点
    buriedPointGame(
      { gameName: currentGame?.game_name, ...defaultTabLogConfig },
      gameId,
      'unified_id',
    );
  }, []);

  const renderContent = () => {
    const activeTab = tabList.find((tab: any) => tab.id === activeId);
    if (!activeTab) return <StudioState />;

    switch (activeTab.pageType) {
      case PAGE_TYPE_DATALAB: {
        const tab = tabList.find((tab: any) => tab.id === activeId);
        return (
          <CommonDashboardReport
            dashboardId={activeId}
            system={typeIdObj?.studio}
            className="dbh-studio-common-dashboard-report"
            reportInfo={{
              uidType: studioId,
              uid: gameId,
              reportPageId: tab?.logConfig?.pageId,
              dashboardName: tab?.nameEn,
              typeId: typeIdObj.studio,
              ...tab?.logConfig,
            }}
          />
        );
      }
      case PAGE_TYPE_PDF:
        return (
          <Pdf
            url={activeId}
            className="db-studio-iframe-wrap"
            style={activeIframeStyle}
          />
        );
      case PAGE_TYPE_LOOKER:
        return (
          <Looker
            url={activeId}
            models={activeTab.models}
            className="db-studio-iframe-wrap"
            style={activeIframeStyle}
          />
        );
      case PAGE_TYPE_TABLEAU:
        return (
          <Tableau
            url={activeId}
            user_id={userInfo?.userid}
            game_code={gameId}
            studio_code={studioId}
            className="db-studio-iframe-wrap"
            style={activeIframeStyle}
          />
        );
      case PAGE_TYPE_AUTH_PATH:
        return (
          <AuthPath
            key={activeId}
            game={currentGame}
            iframeHeight={activeIframeHeight}
            logConfig={activeTab.logConfig}
            page_type={currentMenu?.page_type}
            path={multiReportsPath}
            menu={{
              id: currentMenu?.u_id || currentMenu?.menu_id,
              path: activeTab.authPath || activeTab.path,
              page_type: PAGE_TYPE_AUTH_PATH,
            }}
          />
        );
      default:
        return <StudioState />;
    }
  };

  if (menuPermission === false) {
    return <StudioState isNoPermission={true} />;
  }

  return (
    <Content className="dbi-businessreports">
      <Content.Header className="dbh-multi-reports-tabs-header">
        <Tabs
          ceiling
          tabs={tabs}
          animated={false}
          activeId={activeId}
          onActive={onActive}
          newMarkIdsMappingKey={''}
        />
      </Content.Header>
      <Content.Body full className="dbh-report-full">
        {activeTabConfig?.pageType === PAGE_TYPE_AUTH_PATH ? (
          <div className="dbh-studio-multi-tab-fullscreen" style={activeIframeStyle}>
            {renderContent()}
          </div>
        ) : (
          <FullScreenBtn
            key={activeId}
            className="dbh-studio-multi-tab-fullscreen"
            style={activeIframeStyle}
            attachToIframe={isActiveIframeTab}
            priority={200}
          >
            {renderContent()}
          </FullScreenBtn>
        )}
      </Content.Body>
    </Content>
  );
}
